import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound } from '../utils/soundUtils';
import './SnakeGame.css';

type Direction = 'up' | 'down' | 'left' | 'right';
type Position = { x: number; y: number };

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SPEED = 250; // Slower initial speed

const SnakeGame: React.FC = () => {
  const { earnCoins } = useWallet();
  const [snake, setSnake] = useState<Position[]>([{ x: 10, y: 10 }]);
  const [food, setFood] = useState<Position>({ x: 15, y: 15 });
  const [direction, setDirection] = useState<Direction>('right');
  const [nextDirection, setNextDirection] = useState<Direction>('right');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const gameLoopRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const savedHigh = localStorage.getItem('snake-high-score');
    if (savedHigh) {
      setHighScore(parseInt(savedHigh));
    }
  }, []);

  const generateFood = useCallback((currentSnake: Position[]): Position => {
    let newFood: Position;
    let attempts = 0;
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      };
      attempts++;
      if (attempts > 100) break; // Safety check
    } while (currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }, []);

  const handleGameOver = useCallback(async () => {
    setGameOver(true);
    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }

    setScore(currentScore => {
      setHighScore(currentHigh => {
        if (currentScore > currentHigh) {
          const newHigh = currentScore;
          localStorage.setItem('snake-high-score', newHigh.toString());
          return newHigh;
        }
        return currentHigh;
      });

      // Award coins based on score (only if score > 0)
      // 1 coin per 10 points, minimum 1 coin for any score > 0
      const coins = currentScore > 0 ? Math.max(1, Math.floor(currentScore / 10)) : 0;
      if (coins > 0) {
        earnCoins(coins, `Snake Game - Score: ${currentScore}`, 'snake-game').then(() => {
        if (currentScore >= 100) {
          playCelebrationSound();
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      }).catch(error => {
        console.error('Error awarding coins:', error);
      });
      }

      return currentScore;
    });
  }, [earnCoins]);

  const moveSnake = useCallback(() => {
    if (gameOver || isPaused) return;

    setSnake(prevSnake => {
      const head = { ...prevSnake[0] };
      
      switch (nextDirection) {
        case 'up':
          head.y -= 1;
          break;
        case 'down':
          head.y += 1;
          break;
        case 'left':
          head.x -= 1;
          break;
        case 'right':
          head.x += 1;
          break;
      }

      // Check wall collision
      if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
        handleGameOver();
        return prevSnake;
      }

      // Check self collision
      if (prevSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        handleGameOver();
        return prevSnake;
      }

      const newSnake = [head, ...prevSnake];

      // Check food collision
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => prev + 10);
        // Generate new food using the new snake
        const newFood = generateFood(newSnake);
        setFood(newFood);
        // Increase speed slightly (but keep it slower)
        setSpeed(prev => Math.max(120, prev - 3));
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
    setDirection(nextDirection);
  }, [nextDirection, food, gameOver, isPaused, generateFood, handleGameOver]);

  useEffect(() => {
    if (gameOver || isPaused) {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
      return;
    }

    const intervalId = setInterval(() => {
      moveSnake();
    }, speed);
    
    gameLoopRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      gameLoopRef.current = null;
    };
  }, [speed, gameOver, isPaused, moveSnake]);

  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (gameOver) return;

    const key = e.key;
    let newDirection: Direction | null = null;

    if (key === 'ArrowUp' && nextDirection !== 'down') newDirection = 'up';
    if (key === 'ArrowDown' && nextDirection !== 'up') newDirection = 'down';
    if (key === 'ArrowLeft' && nextDirection !== 'right') newDirection = 'left';
    if (key === 'ArrowRight' && nextDirection !== 'left') newDirection = 'right';
    if (key === ' ' || key === 'Space') {
      e.preventDefault();
      setIsPaused(prev => !prev);
      return;
    }

    if (newDirection) {
      e.preventDefault();
      setNextDirection(newDirection);
    }
  }, [nextDirection, gameOver]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const resetGame = () => {
    const initialSnake = [{ x: 10, y: 10 }];
    setSnake(initialSnake);
    setFood(generateFood(initialSnake));
    setDirection('right');
    setNextDirection('right');
    setScore(0);
    setGameOver(false);
    setIsPaused(false);
    setSpeed(INITIAL_SPEED);
    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
  };

  const handleDirectionButton = (dir: Direction) => {
    if (gameOver || isPaused) return;
    
    if (
      (dir === 'up' && nextDirection !== 'down') ||
      (dir === 'down' && nextDirection !== 'up') ||
      (dir === 'left' && nextDirection !== 'right') ||
      (dir === 'right' && nextDirection !== 'left')
    ) {
      setNextDirection(dir);
    }
  };

  return (
    <IonPage>
      <AppHeader title="Snake Game" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="snake-game-container">
          <IonCard className="score-card">
            <IonCardContent>
              <div className="score-row">
                <div className="score-item">
                  <div className="score-label">Score</div>
                  <div className="score-value">{score}</div>
                </div>
                <div className="score-item">
                  <div className="score-label">High Score</div>
                  <div className="score-value">{highScore}</div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          <div className="game-board">
            <div className="snake-grid">
              {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, index) => {
                const x = index % GRID_SIZE;
                const y = Math.floor(index / GRID_SIZE);
                const isSnake = snake.some(seg => seg.x === x && seg.y === y);
                const isHead = snake[0]?.x === x && snake[0]?.y === y;
                const isFood = food.x === x && food.y === y;

                return (
                  <div
                    key={index}
                    className={`cell ${isSnake ? 'snake' : ''} ${isHead ? 'head' : ''} ${isFood ? 'food' : ''}`}
                  />
                );
              })}
            </div>
          </div>

          {gameOver && (
            <IonCard className="game-over-card">
              <IonCardContent>
                <h2>Game Over!</h2>
                <p>Final Score: {score}</p>
                <IonButton expand="block" color="primary" onClick={resetGame}>
                  Play Again
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {!gameOver && (
            <div className="game-controls">
              <div className="control-buttons">
                <IonButton
                  onClick={() => handleDirectionButton('up')}
                  disabled={isPaused}
                  className="control-btn up"
                >
                  ↑
                </IonButton>
                <div className="horizontal-controls">
                  <IonButton
                    onClick={() => handleDirectionButton('left')}
                    disabled={isPaused}
                    className="control-btn"
                  >
                    ←
                  </IonButton>
                  <IonButton
                    onClick={() => setIsPaused(prev => !prev)}
                    className="control-btn pause"
                  >
                    {isPaused ? '▶' : '⏸'}
                  </IonButton>
                  <IonButton
                    onClick={() => handleDirectionButton('right')}
                    disabled={isPaused}
                    className="control-btn"
                  >
                    →
                  </IonButton>
                </div>
                <IonButton
                  onClick={() => handleDirectionButton('down')}
                  disabled={isPaused}
                  className="control-btn down"
                >
                  ↓
                </IonButton>
              </div>
            </div>
          )}

          <IonCard className="info-card">
            <IonCardContent>
              <p><strong>How to Play:</strong></p>
              <p>Use arrow keys or buttons to control the snake. Eat food to grow and score points!</p>
              <p>Press Space or Pause button to pause/resume.</p>
              <p className="coin-info">💰 Coins: 1 coin per 10 points (minimum 1 coin)!</p>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default SnakeGame;

